import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, RotateCcw, Shuffle, Moon, Download, Copy } from 'lucide-react';
import { toast } from 'sonner';
import ShareButtons from './ShareButtons';
import GiftModal from './GiftModal';
import { FLASHCARDS, FLASHCARD_COUNT } from '../data/flashcards';
import '../styles/pull-a-card.css';

const SHARE_GRAPHIC = '/share-pull-a-card-square.png';
const SHARE_CAPTION =
  "🃏 Pull your FREE Card of the Day at The Alchemy Village. A daily truth from my F.U.C.K. T.H.A.T.T. deck — come sit with what your spirit already knows. 🌙\n\nFree forever 👉 thealchemyvillage.com/pull-a-card\n\n#TheAlchemyVillage #CardOfTheDay #FUCKTHATT #DailyAffirmation #WomenOver50";

/* Inline Tree of Life mark for the face-down deck back */
function TreeOfLife({ size = 88 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" aria-hidden="true" className="pac-tree">
      <circle cx="50" cy="50" r="46" stroke="currentColor" strokeWidth="1" opacity="0.6" />
      <circle cx="50" cy="50" r="38" stroke="currentColor" strokeWidth="0.6" opacity="0.4" />
      <path d="M50 78 L50 46" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M50 52 C42 46 34 44 30 34 M50 52 C58 46 66 44 70 34 M50 46 C44 40 40 34 40 26 M50 46 C56 40 60 34 60 26 M50 40 C50 32 50 28 50 22"
        stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="30" cy="33" r="3" fill="currentColor" /><circle cx="70" cy="33" r="3" fill="currentColor" />
      <circle cx="40" cy="25" r="2.6" fill="currentColor" /><circle cx="60" cy="25" r="2.6" fill="currentColor" />
      <circle cx="50" cy="21" r="3.2" fill="currentColor" /><circle cx="50" cy="46" r="2.4" fill="currentColor" />
      <path d="M44 78 Q50 74 56 78" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

const origin = typeof window !== 'undefined' ? window.location.origin : '';
const STORAGE_KEY = 'tav_card_of_day';

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function timeUntilMidnight() {
  const now = new Date();
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  const diff = Math.max(0, midnight - now);
  const h = Math.floor(diff / 3.6e6);
  const m = Math.floor((diff % 3.6e6) / 6e4);
  const s = Math.floor((diff % 6e4) / 1000);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function readSavedCard() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (saved && saved.date === todayKey()) {
      return FLASHCARDS.find((c) => c.id === saved.id) || null;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export default function PullACardPage() {
  const [card, setCard] = useState(readSavedCard);
  const [showBack, setShowBack] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [pulledToday, setPulledToday] = useState(() => readSavedCard() !== null);
  const [countdown, setCountdown] = useState(timeUntilMidnight());
  const [buyingDeck, setBuyingDeck] = useState(false);

  const buyDigitalDeck = useCallback(async () => {
    setBuyingDeck(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: 'digital-card-deck', origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not start checkout. Please try again.');
        setBuyingDeck(false);
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
      setBuyingDeck(false);
    }
  }, []);

  // Live countdown to midnight (only while locked)
  useEffect(() => {
    if (!pulledToday) return undefined;
    const t = window.setInterval(() => setCountdown(timeUntilMidnight()), 1000);
    return () => window.clearInterval(t);
  }, [pulledToday]);

  // Page title + meta for this route (browser tab + search/social text)
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Pull a Card · Your Free Card of the Day — The Alchemy Village';
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute('content') : null;
    if (meta) {
      meta.setAttribute(
        'content',
        "Pull your free Card of the Day from Cabrini's F.U.C.K. T.H.A.T.T. deck — a daily truth to sit with. Free forever at The Alchemy Village.",
      );
    }
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc !== null) meta.setAttribute('content', prevDesc);
    };
  }, []);

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_CAPTION);
      toast.success('Caption copied — paste it into your Instagram or Facebook post.');
    } catch {
      toast.error('Copy failed. Long-press the text to copy.');
    }
  };

  const pull = useCallback(() => {
    if (pulledToday) return;
    setPulling(true);
    setShowBack(false);
    const next = FLASHCARDS[Math.floor(Math.random() * FLASHCARD_COUNT)];
    window.setTimeout(() => {
      setCard(next);
      setPulling(false);
      setPulledToday(true);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ date: todayKey(), id: next.id }));
      } catch {
        /* ignore */
      }
    }, 720);
  }, [pulledToday]);

  const shareUrl = `${origin}/pull-a-card`;
  const shareImage = card ? `${origin}${card.front}` : '';

  return (
    <section className="pac-page" data-testid="pull-a-card-page">
      {/* Ambient starfield */}
      <div className="pac-stars" aria-hidden="true" />

      <header className="pac-hero">
        <p className="pac-eyebrow"><Sparkles size={14} strokeWidth={2} /> Your Card of the Day · Free Forever</p>
        <h1 className="pac-title" data-testid="pull-a-card-title">Pull a Card</h1>
        <p className="pac-sub">
          Quiet your mind, take a breath, and ask: <em>what do I WANT to know today?</em>
          {' '}Then pull a card from Cabrini’s <strong>F.U.C.K. T.H.A.T.T.</strong> deck and let it speak.
        </p>
      </header>

      <div className="pac-stage">
        {/* ── Face-down deck (idle) ── */}
        {!card && !pulling && (
          <button
            type="button"
            className="pac-deck"
            onClick={pull}
            data-testid="pull-card-deck"
            aria-label="Pull a card"
          >
            <span className="pac-deck-layer pac-deck-layer--3" aria-hidden="true" />
            <span className="pac-deck-layer pac-deck-layer--2" aria-hidden="true" />
            <span className="pac-deck-face">
              <span className="pac-deck-frame">
                <span className="pac-deck-brand">F.U.C.K. T.H.A.T.T.</span>
                <TreeOfLife />
                <span className="pac-deck-cta">Tap to Pull</span>
                <span className="pac-deck-byline">Cabrini Schnyder</span>
              </span>
            </span>
          </button>
        )}

        {/* ── Shuffling ── */}
        {pulling && (
          <div className="pac-deck pac-deck--shuffling" data-testid="pull-card-shuffling" aria-live="polite">
            <span className="pac-deck-layer pac-deck-layer--3" aria-hidden="true" />
            <span className="pac-deck-layer pac-deck-layer--2" aria-hidden="true" />
            <span className="pac-deck-face">
              <span className="pac-deck-frame">
                <Shuffle size={40} className="pac-shuffle-icon" />
                <span className="pac-deck-cta">The Village is choosing…</span>
              </span>
            </span>
          </div>
        )}

        {/* ── Revealed card ── */}
        {card && !pulling && (
          <div className={`pac-card-flip ${showBack ? 'is-flipped' : ''}`} data-testid="pull-card-revealed">
            <div className="pac-card-inner">
              <div className="pac-card-side pac-card-front">
                <img src={card.front} alt={card.alt} data-testid="pull-card-front-img" />
              </div>
              <div className="pac-card-side pac-card-back">
                <img src={card.back} alt={`${card.alt} — Cabrini Check-In`} data-testid="pull-card-back-img" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Controls ── */}
      {card && !pulling && (
        <div className="pac-controls" data-testid="pull-card-controls">
          <button
            type="button"
            className="village-btn village-btn--secondary pac-flip-btn"
            onClick={() => setShowBack((v) => !v)}
            data-testid="pull-card-flip-btn"
          >
            <RotateCcw size={16} strokeWidth={2.2} />
            {showBack ? 'See the Card' : 'Cabrini’s Check-In'}
          </button>
        </div>
      )}

      {/* ── Daily lock ── */}
      {card && !pulling && (
        <div className="pac-locked" data-testid="pull-card-locked">
          <p className="pac-locked-line">
            <Moon size={15} strokeWidth={2} /> This is your card for today.
          </p>
          <p className="pac-locked-sub">
            A new card awakens at midnight — come back tomorrow.
          </p>
          <p className="pac-countdown" data-testid="pull-card-countdown">
            Next pull in <strong>{countdown}</strong>
          </p>
        </div>
      )}

      {card && !pulling && (
        <p className="pac-counter" data-testid="pull-card-counter">
          Card {card.id} of {FLASHCARD_COUNT} · sit with it today
        </p>
      )}

      {/* ── Share ── */}
      {card && !pulling && (
        <div className="pac-share">
          <ShareButtons
            url={shareUrl}
            title="I pulled a F.U.C.K. T.H.A.T.T. card today 🌙"
            text="Pull your own daily card from Cabrini Schnyder's deck at The Alchemy Village."
            image={shareImage}
            variant="gold"
          />
        </div>
      )}

      {/* ── Spread the word kit ── */}
      <div className="pac-promote" data-testid="pull-card-promote">
        <div className="pac-promote-preview">
          <img src={SHARE_GRAPHIC} alt="Shareable Card of the Day graphic for Instagram and Facebook" loading="lazy" />
        </div>
        <div className="pac-promote-body">
          <p className="pac-promote-eyebrow"><Sparkles size={13} strokeWidth={2} /> Spread the Word</p>
          <h2 className="pac-promote-title">Post it. Grow the Village.</h2>
          <p className="pac-promote-sub">
            Grab this ready-made graphic and caption — post it to Instagram or
            Facebook to invite your people to pull their own free card.
          </p>
          <div className="pac-promote-actions">
            <a
              href={SHARE_GRAPHIC}
              download="alchemy-village-card-of-the-day.png"
              className="village-btn village-btn--primary"
              data-testid="pull-card-download-graphic"
            >
              <Download size={16} strokeWidth={2.2} /> Download Graphic
            </a>
            <button
              type="button"
              className="village-btn village-btn--secondary"
              onClick={copyCaption}
              data-testid="pull-card-copy-caption"
            >
              <Copy size={16} strokeWidth={2.2} /> Copy Caption
            </button>
          </div>
        </div>
      </div>

      {/* ── Physical deck CTA ── */}
      <div className="pac-deck-promo" data-testid="pull-card-deck-promo">
        <div className="pac-deck-promo-fan" aria-hidden="true">
          <img src="/cards/card_07_front.webp" alt="" className="pac-fan pac-fan--l" />
          <img src="/cards/card_01_front.webp" alt="" className="pac-fan pac-fan--c" />
          <img src="/cards/card_22_front.webp" alt="" className="pac-fan pac-fan--r" />
        </div>
        <div className="pac-deck-promo-body">
          <p className="pac-deck-promo-eyebrow">Take the whole deck home</p>
          <h2 className="pac-deck-promo-title">The F.U.C.K. T.H.A.T.T. Card Deck</h2>
          <p className="pac-deck-promo-sub">
            Love your daily pull? Get <strong>all 50 cards</strong> as an instant
            print-ready PDF for <strong>$14.99</strong> — or join the waitlist for
            the gold-foiled boxed physical deck ($29.99, coming soon).
          </p>
          <div className="pac-promote-actions">
            <button
              type="button"
              className="village-btn village-btn--primary"
              onClick={buyDigitalDeck}
              disabled={buyingDeck}
              data-testid="pull-card-buy-digital-deck"
            >
              {buyingDeck ? 'Opening checkout…' : (<><Download size={16} strokeWidth={2.2} /> Buy Digital Deck · $14.99</>)}
            </button>
            <GiftModal
              productId="digital-card-deck"
              productLabel="the Digital Deck"
              buttonLabel="Gift the Deck"
              buttonClassName="village-btn village-btn--secondary"
              testId="pull-card-gift-deck-btn"
            />
            <Link to="/merch" className="village-btn village-btn--secondary" data-testid="pull-card-shop-deck-btn">
              Physical Deck Waitlist
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
